// Хуки для работы со справочниками через TanStack Query
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import * as refsApi from '@/api/refs'
import { useOrgScopedQueryKey } from '@/hooks/useOrgScopedQueryKey'
import { getApiErrorDetail } from '@/utils/apiError'

// Филиалы
export const useFilials = () => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['filials', orgContextKey],
    queryFn: refsApi.fetchFilials,
  })
}

export const useCreateFilial = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: refsApi.createFilial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['filials'] })
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при создании филиала')
    },
  })
}

export const useUpdateFilial = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ fil_code, data }: { fil_code: number; data: { fil_name?: string } }) =>
      refsApi.updateFilial(fil_code, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['filials'] })
      message.success('Филиал обновлён')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при обновлении филиала')
    },
  })
}

export const useDeleteFilial = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: refsApi.deleteFilial,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['filials'] })
      message.success('Филиал удалён')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при удалении филиала')
    },
  })
}

// Службы
export const useServices = () => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['services', orgContextKey],
    queryFn: () => refsApi.fetchServices(),
  })
}

export const useCreateService = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: refsApi.createService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] })
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при создании службы')
    },
  })
}

export const useUpdateService = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ serv_code, data }: { serv_code: number; data: { serv_name?: string; description?: string; is_active?: boolean } }) =>
      refsApi.updateService(serv_code, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] })
      message.success('Служба обновлена')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при обновлении службы')
    },
  })
}

export const useDeleteService = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (serv_code: number) => refsApi.deleteService(serv_code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] })
      message.success('Служба удалена')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при удалении службы')
    },
  })
}

// Отделения
export const useDepartments = (filters?: refsApi.DepartmentFilters) => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['departments', orgContextKey, filters],
    queryFn: () => refsApi.fetchDepartments(filters),
  })
}

export const useUpdateDepartment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id_otd, ot_per, data }: { id_otd: string; ot_per: string; data: Partial<refsApi.Department> }) =>
      refsApi.updateDepartment(id_otd, ot_per, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      message.success('Отделение обновлено')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при обновлении отделения')
    },
  })
}

export const useBatchUpdateDepartments = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: refsApi.batchUpdateDepartments,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] })
      message.success('Отделения обновлены')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при массовом обновлении')
    },
  })
}

// Плановые показатели
export const usePlanIndicators = (filters?: refsApi.PlanIndicatorFilters) => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['plan-indicators', orgContextKey, filters],
    queryFn: () => refsApi.fetchPlanIndicators(filters),
  })
}

export const useUpdatePlanIndicators = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: refsApi.updatePlanIndicators,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan-indicators'] })
      message.success('Показатели обновлены')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при обновлении показателей')
    },
  })
}
